import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/shared/UIComponents';
import { AuthHero } from '@/components/auth/AuthHero';
import { AuthFooter } from '@/components/auth/AuthFooter';

export function SignupPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) { setError('Email is required'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }

    setLoading(true);
    try {
      await signUp(email, password);
      setSuccess(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-surface-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center mx-auto mb-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-emerald-600 dark:text-emerald-400" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M22 4L12 14.01l-3-3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-surface-900 mb-2">Check your email</h1>
          <p className="text-sm text-surface-500 mb-6">
            We've sent a confirmation link to <span className="text-surface-800 font-medium">{email}</span>.
            Click the link to activate your account.
          </p>
          <Link to="/auth/login" className="btn-primary inline-flex">Back to Sign In</Link>
        </div>
        </div>
        <AuthFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 flex flex-col lg:flex-row">
      <AuthHero
        headline={<>Get started with<br />DiscoveryGem Platform</>}
        subheadline="Create your admin account to start managing tenants, branding, and features across your white-label SaaS platform."
      />

      {/* Right panel — form + footer stacked */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img src="/logo.svg" alt="DiscoveryGem" className="w-9 h-9 dark:brightness-0 dark:invert" />
            <div>
              <div className="font-semibold text-surface-900">DiscoveryGem</div>
              <div className="text-[10px] text-surface-500 uppercase tracking-widest">Platform</div>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-surface-900 mb-2">Create an account</h1>
          <p className="text-sm text-surface-500 mb-8">Set up your platform admin account</p>

          {error && (
            <div className="mb-6 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-sm text-red-600 dark:text-red-400">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="input-label">Email</label>
              <input type="email" className="input-field" placeholder="admin@company.com"
                value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" autoFocus />
            </div>
            <div>
              <label className="input-label">Password</label>
              <input type="password" className="input-field" placeholder="Minimum 6 characters"
                value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" />
            </div>
            <div>
              <label className="input-label">Confirm Password</label>
              <input type="password" className="input-field" placeholder="••••••••"
                value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} autoComplete="new-password" />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full py-3">
              {loading ? <Spinner size="sm" /> : 'Create Account'}
            </button>
          </form>

          <p className="text-sm text-surface-500 text-center mt-8">
            Already have an account?{' '}
            <Link to="/auth/login" className="text-brand-600 hover:text-brand-700 font-medium transition-colors">
              Sign in
            </Link>
          </p>
        </div>
        </div>
        <AuthFooter />
      </div>
    </div>
  );
}
